import { _ALIAS_SUFFIX, _NEW_LINE, _SPACE_CARD } from "../components/constants";

/**
 * Represents the properties of a grid cell.
 */
export interface GridCellProps {
    index: number;
    label: string;
    image: string;
    color: string;
    isEmpty: boolean;
    isEditing: boolean;
    isCard: boolean;
    isSelected: boolean;
}

/**
 * The default properties for a grid cell.
 */
export const defaultCellProps: GridCellProps = {
    index: -1,
    label: '',
    image: '',
    color: '',
    isEmpty: true,
    isEditing: false,
    isCard: false,
    isSelected: false,
};

/**
 * Converts a list of labels into a 1D array of GridCellProps.
 * Each string label in the list will be converted into a GridCellProps object with the label property set to the string value.
 * 
 * @param {string[]} labels - The string list to convert.
 * @returns {GridCellProps[]} - The 1D array of GridCellProps.
 */
export function convertLabelsToGridCellList(labels: string[]): GridCellProps[] {
    return labels.map((str, index: number) => {
        const skipMe = str === undefined || str === null || str === _SPACE_CARD || str === "";
        return {
            index: index,
            label: skipMe ? '' : str,
            image: '',
            color: '',
            isEmpty: skipMe ? true : false,
            isEditing: false,
            isCard: skipMe ? false : true,
            isSelected: false,
        };
    });
}

/**
 * Iterates over the input GridCellProps[][] and gathers all the labels into a single list.
 * Maps empty cells to _SPACE_CARD only if there is at least one non-empty cell at any later point.
 * Determines the highest index of non-empty cells initially to optimize the mapping process.
 * 
 * @param {GridCellProps[][]} grid - The 2D array of GridCellProps.
 * @returns {string[]} - The list of labels.
 */
export function gatherLabels(grid: GridCellProps[][]): string[] {
    const labels: string[] = [];
    let lastNonEmptyCellIndex = -1;

    // First pass to determine the last non-empty cell's index
    for (let rowIndex = 0; rowIndex < grid.length; rowIndex++) {
        for (let colIndex = 0; colIndex < grid[rowIndex].length; colIndex++) {
            const cell = grid[rowIndex][colIndex];
            if (!cell.isEmpty) {
                lastNonEmptyCellIndex = rowIndex * grid[rowIndex].length + colIndex;
            }
        }
    }

    // Gather labels with the knowledge of when to map empty cells to _SPACE_CARD
    let currentIndex = 0;
    for (const row of grid) {
        for (const cell of row) {
            if (cell.isEmpty && currentIndex <= lastNonEmptyCellIndex) {
                labels.push(_SPACE_CARD);
            } else if (!cell.isEmpty) {
                labels.push(cell.label);
            }
            currentIndex++;
        }
    }

    return labels;
}

/**
 * Retrieves the indices of new line characters in the input list.
 * 
 * @param {string[]} inputList - The input list of strings.
 * @returns {number[]} - The indices of new line characters.
 */
export function getNewLineIndices(inputList: string[]): number[] {
    return inputList.reduce((acc: number[], node, index) => {
        if (node === _NEW_LINE) acc.push(index);
        return acc;
    }, []);
}

/**
 * Checks if a label is empty.
 * 
 * @param {string} label - The label to check.
 * @returns {boolean} - True if the label is empty, false otherwise.
 */
export function isLabelEmpty(label: string): boolean {
    return label.trim() === '' || label === _SPACE_CARD || label === _NEW_LINE;
}  
  
  

/**
 * Transforms a 1D array into a 2D array based on the specified number of columns, breaking points, and a pseudo total cell count.
 * Optionally allows or disallows full "empty" rows composed entirely of the placeholder element based on the `allowFullPlaceholderRows` flag.
 * The `pseudoTotalCells` parameter specifies a desired total number of cells, which the function uses to determine the overall size of the 2D array, completing the last row if necessary.
 * Any remaining elements in the last row will be filled with the placeholder element to meet the pseudo total number of cells.
 * 
 * @template T - The type of elements in the array.
 * @param {T[]} inputList - The input array to transform.
 * @param {number} nCols - The number of columns in the resulting 2D array.
 * @param {number[]} breakingPoints - An array of indices where the rows should break.
 * @param {T} placeholderElement - The placeholder element to fill the remaining spaces in each row.
 * @param {boolean} allowFullPlaceholderRows - Flag to determine whether rows that are entirely placeholders are allowed in the output.
 * @param {number} pseudoTotalCells - The pseudo total number of cells desired in the output 2D array.
 * @returns {T[][]} - The transformed 2D array.
 */
export function transformArrayTo2D<T>(
    inputList: T[],
    nCols: number,
    breakingPoints: number[],
    placeholderElement: T,
    allowFullPlaceholderRows: boolean,
    pseudoTotalCells: number
): T[][] {
    let result: T[][] = []; // Initialize the resulting 2D array
    let currentRow: T[] = []; // Initialize the current row being filled
    let totalCells = 0; // Keep track of the total cells added to the 2D array, including placeholders

    // Helper function to fill the current row with placeholders up to nCols
    const fillRowWithPlaceholders = () => {
        while (currentRow.length < nCols) {
            currentRow.push(placeholderElement);
            totalCells++;
        }
    };

    // Helper function to add the current row to the result, respecting the allowFullPlaceholderRows flag
    const commitRow = () => {
        if (currentRow.length > 0) { // Only add non-empty rows
            if (allowFullPlaceholderRows || currentRow.some(cell => cell !== placeholderElement)) {
                result.push(currentRow);
            }
        }
        currentRow = []; // Reset currentRow for the next entries
    };

    inputList.forEach((item, index) => {
        // Add item to the row
        currentRow.push(item);
        totalCells++;

        // Check if next index is a breaking point or if we're at the end of the input list
        if (breakingPoints.includes(index + 1) || index === inputList.length - 1) {
            fillRowWithPlaceholders(); // Fill the rest of the row with placeholders if needed
            commitRow();
        } else if (currentRow.length === nCols) { // If the row is full, commit it and start a new one
            commitRow();
        }
    });

    // After processing inputList, if there are remaining cells to reach pseudoTotalCells, add additional rows or placeholders
    while (totalCells < pseudoTotalCells) {
        if (currentRow.length === nCols) { // Commit the full row and start a new one if necessary
            commitRow();
        }
        currentRow.push(placeholderElement); // Add placeholder elements to the current or a new row
        totalCells++;
    }

    // Commit any partially filled row left after reaching pseudoTotalCells
    if (currentRow.length > 0) {
        fillRowWithPlaceholders(); // This ensures the last row is properly filled up to nCols
        commitRow();
    }

    return result;
}


export function getPropsWhenConfirmed(inputValue:string){
    if(inputValue === "") return {
        isEmpty: true,
        isEditing: false,
        isCard: false,
    } 
    return {
        label: inputValue != _NEW_LINE ? randomAliasLabel(inputValue) : _NEW_LINE,
        isEmpty: false,
        isEditing: false,
        isCard: true,
        isSelected: false
    }
}

/* Adds a suffix _ALIAS_SUFFIX+random number of 4 digits to the label which will not be visible
to ensure label uniqueness */
export function randomAliasLabel(label: string){
    // Check first if label was already an alias
    if(label.includes(_ALIAS_SUFFIX)) return label;
    let currentTime4Digits = new Date().getTime().toString().slice(-4);
    return label + _ALIAS_SUFFIX + currentTime4Digits;
}

export function reRandomizeAliasLabel(label: string){
    // Take the label root, i.e. excluding all what follows the alias chars
    let root = label.split(_ALIAS_SUFFIX)[0];
    // create a new alias
    return randomAliasLabel(root);
}

export function reindexCells(cells2D: GridCellProps[][], nCols:number){
    return cells2D.map((row, i) => row.map((cell, j) => ({ ...cell, index: i * nCols + j })));
}

/* Marks selected cells as empty */
export function makeSelectedCellsEmpty(cells: GridCellProps[][]): GridCellProps[][] {
    return cells.map(row => row.map(cell => {
      if (cell.isSelected) {
        return { ...defaultCellProps};
      }
      return cell;
    }));
  }


export function removeSelectedCellsEntireRows(cells: GridCellProps[][]): GridCellProps[][] {
    return cells.filter(row => !row.some(cell => cell.isSelected));
}